'use client';

import { User } from '@api/models';
import { useTranslations } from 'next-intl';
import { usePathname, useRouter } from 'next/navigation';
import React from 'react';

import { ListingCardsScreen } from '@ui/sections/listings';
import { useAuthenticatedNavigation } from '@website/hooks/useAuthenticatedNavigation';
import { Activity } from '@website/services/activities.service';

interface ListingCardsScreenWrapperProps {
  /**
   * Whether to show the activity count text
   * @default true
   */
  showActivityCount?: boolean;

  /**
   * Whether to hide the instructor name on activity cards
   * @default false
   */
  hideInstructor?: boolean;

  /**
   * Array of activity objects to display
   */
  activities: {
    id: string;
    imageUrl: string;
    title: string;
    professionalName: string;
    isAtHome?: boolean;
    sessionsSinceLastYear: number;
    /** `null`/`undefined` → no priced session yet; price block is hidden. */
    startingPrice?: number | null;
    /**
     * City/address shown when the activity is venue-based. Either `city`
     * or `location.address` may be provided — the wrapper falls back to
     * `location.address` when `city` is missing.
     */
    city?: string;
    location?: { address?: string } | null;
  }[];

  /**
   * Total count of activities (may be different from activities.length if paginated)
   */
  totalCount: number;

  /**
   * Whether the activities are currently loading
   */
  isLoading?: boolean;

  /**
   * Whether there was an error loading the activities
   */
  isError?: boolean;

  /**
   * Handler for when an activity card is hovered
   */
  onActivityHover?: (activityId: string) => void;

  /**
   * Handler for when hover leaves an activity card
   */
  onActivityLeaveHover?: () => void;

  /**
   * ID of the currently hovered activity to highlight it
   */
  hoveredActivityId?: string;

  /**
   * ID of the currently selected activity
   */
  selectedActivityId?: string;

  /**
   * Callback to register a ref for an activity card
   */
  registerCardRef?: (id: string, element: HTMLDivElement | null) => void;

  /**
   * Handler for when activity selection changes
   */
  onActivitySelect?: (activityId: string) => void;

  /**
   * Current user for authentication-aware navigation
   */
  user?: User | null;

  /**
   * Top activities to show in empty state (e.g., top by booking count)
   */
  topActivities?: Activity[];

  /**
   * Whether top activities are currently loading
   */
  isTopActivitiesLoading?: boolean;
}

/**
 * Client component wrapper for ListingCardsScreen
 * This handles the client-side interaction logic for the ListingCardsScreen component
 */
export const ListingCardsScreenWrapper: React.FC<ListingCardsScreenWrapperProps> = ({
  activities,
  totalCount,
  isLoading = false,
  isError = false,
  onActivityHover,
  onActivityLeaveHover,
  hoveredActivityId,
  selectedActivityId,
  registerCardRef,
  onActivitySelect,
  showActivityCount = true,
  hideInstructor = false,
  user,
  topActivities,
  isTopActivitiesLoading = false
}) => {
  const handleActivityClick = (id: string) => {
    console.log(`Activity ${id} selected`);

    // Always use the onActivitySelect handler if provided
    if (onActivitySelect) {
      onActivitySelect(id);
    }
    // Removed URL parameter manipulation for better client-side performance
  };

  const router = useRouter();
  const pathname = usePathname();
  const locale = pathname.split('/')[1]; // Extract locale from current path

  // Authenticated navigation hook for slots
  const { navigateWithAuth } = useAuthenticatedNavigation({
    user: user ?? null
  });

  const handleViewActivity = (id: string) => {
    console.log(`Navigate to activity ${id} details`);
    // Use the Next.js router for navigation with the current locale
    router.push(`/${locale}/activity/${id}`);
  };

  const handleViewSlots = (id: string) => {
    console.log(`Navigate to activity ${id} slots with auth`);
    // Use authenticated navigation for slots booking
    navigateWithAuth(`/activity/${id}/slots`);
  };

  const lsTranslations = useTranslations('ListingCardsScreen');
  const activityCardTranslations = useTranslations('ActivityCard');

  // Transform topActivities to match the format expected by ListingCardsScreen
  const transformedTopActivities = topActivities?.map((act) => ({
    id: act.id,
    title: act.title,
    imageUrl: act.imageUrl,
    professionalName: act.professionalName,
    isAtHome: act.isAtHome,
    startingPrice: act.startingPrice,
    sessionsSinceLastYear: act.sessionsSinceLastYear || 0,
    city: act.location?.address
  }));

  // Normalize address: callers may supply either `city` or `location.address`.
  // `ListingCardsScreen` only reads `city` and otherwise falls back to the
  // generic "Au lieu" label, so we resolve both shapes here in one place.
  const transformedActivities = activities.map(({ location, ...act }) => ({
    ...act,
    city: act.city ?? location?.address ?? undefined
  }));

  return (
    <ListingCardsScreen
      activities={transformedActivities}
      totalCount={totalCount}
      isLoading={isLoading}
      isError={isError}
      showActivityCount={showActivityCount}
      hideInstructor={hideInstructor}
      onActivityClick={handleActivityClick}
      activitiesFoundSingularPrefix={lsTranslations('activitiesFoundSingularPrefix')}
      activitiesFoundSingularSuffix={lsTranslations('activitiesFoundSingularSuffix')}
      activitiesFoundPluralPrefix={lsTranslations('activitiesFoundPluralPrefix')}
      activitiesFoundPluralSuffix={lsTranslations('activitiesFoundPluralSuffix')}
      noActivitiesFoundTitle={lsTranslations('noActivitiesFoundTitle')}
      noActivitiesFoundText={lsTranslations('noActivitiesFound')}
      loadingText={lsTranslations('loading')}
      atHomeText={activityCardTranslations('atHome')}
      atVenueText={activityCardTranslations('atVenue')}
      sessionsInYearPrefix={activityCardTranslations('sessionsInYearPrefix')}
      sessionsInYearMiddle={activityCardTranslations('sessionsInYearMiddle')}
      sessionsInYearSuffix={activityCardTranslations('sessionsInYearSuffix')}
      onwardsText={activityCardTranslations('onwards')}
      professionalPrefixText={activityCardTranslations('professionalPrefix')}
      viewActivityButtonText={activityCardTranslations('viewActivityButton')}
      availableSlotsButtonText={activityCardTranslations('availableSlotsButton')}
      onActivityHover={onActivityHover}
      onActivityLeaveHover={onActivityLeaveHover}
      hoveredActivityId={hoveredActivityId}
      selectedActivityId={selectedActivityId}
      registerCardRef={registerCardRef}
      onViewActivity={handleViewActivity}
      onViewSlots={handleViewSlots}
      topActivities={transformedTopActivities}
      topActivitiesTitle={lsTranslations('topActivitiesTitle')}
      isTopActivitiesLoading={isTopActivitiesLoading}
      locale={locale}
    />
  );
};
